import React, { useState } from 'react';
import './StripePayment.css';

interface StripePaymentProps {
  clientSecret?: string;
  amount: number;
  bookingId?: string;
  onSuccess: (paymentMethodId: string) => void;
  onError: (error: string) => void;
  onCancel?: () => void;
}

const StripePayment: React.FC<StripePaymentProps> = ({ 
  clientSecret, 
  amount, 
  bookingId, 
  onSuccess, 
  onError,
  onCancel 
}) => {
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvc, setCvc] = useState('');
  const [name, setName] = useState('');
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(' ') : value;
  };

  const formatExpiryDate = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.slice(0, 2) + (v.length > 2 ? '/' + v.slice(2, 4) : '');
    }
    return v;
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!name.trim()) newErrors.name = 'Name ist erforderlich';
    if (!cardNumber.replace(/\s/g, '').match(/^\d{16}$/)) {
      newErrors.cardNumber = 'Ungültige Kartennummer';
    }
    if (!expiryDate.match(/^(0[1-9]|1[0-2])\/\d{2}$/)) {
      newErrors.expiryDate = 'Format: MM/JJ';
    }
    if (!cvc.match(/^\d{3,4}$/)) {
      newErrors.cvc = 'Ungültiger CVC';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setProcessing(true);
    
    // Simulate payment processing
    setTimeout(() => {
      // Check for test card numbers
      const cleanCardNumber = cardNumber.replace(/\s/g, '');
      
      if (cleanCardNumber === '4000000000000002') {
        onError('Ihre Karte wurde abgelehnt.');
        setProcessing(false);
        return;
      }
      
      if (cleanCardNumber === '4000002760003184') {
        // Simulate 3D Secure
        alert('3D Secure Authentifizierung erforderlich (Mock)');
      }
      
      // Success case
      const paymentMethodId = 'pm_' + Date.now();
      onSuccess(paymentMethodId);
      setProcessing(false);
    }, 2000);
  };

  return (
    <div className="stripe-payment-container">
      <div className="payment-header">
        <h3>💳 Kreditkartenzahlung</h3>
        <div className="secure-badge">
          <span className="lock-icon">🔒</span>
          <span>Sichere Verbindung</span>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="payment-form">
        <div className="form-group">
          <label htmlFor="name">Karteninhaber</label>
          <input
            id="name"
            type="text"
            placeholder="Max Mustermann"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={processing}
            className={errors.name ? 'error' : ''}
            required
          />
          {errors.name && <span className="error-message">{errors.name}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="cardNumber">Kartennummer</label>
          <div className="card-input-wrapper">
            <input
              id="cardNumber"
              type="text"
              placeholder="4242 4242 4242 4242"
              value={cardNumber}
              onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
              maxLength={19}
              disabled={processing}
              className={errors.cardNumber ? 'error' : ''}
              required
            />
            <div className="card-brands">
              <span className="card-brand visa">💳</span>
              <span className="card-brand mastercard">💳</span>
              <span className="card-brand amex">💳</span>
            </div>
          </div>
          {errors.cardNumber && <span className="error-message">{errors.cardNumber}</span>}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="expiryDate">Ablaufdatum</label>
            <input
              id="expiryDate"
              type="text"
              placeholder="MM/JJ"
              value={expiryDate}
              onChange={(e) => setExpiryDate(formatExpiryDate(e.target.value))}
              maxLength={5}
              disabled={processing}
              className={errors.expiryDate ? 'error' : ''}
              required
            />
            {errors.expiryDate && <span className="error-message">{errors.expiryDate}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="cvc">CVC</label>
            <input
              id="cvc"
              type="text"
              placeholder="123"
              value={cvc}
              onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
              maxLength={4}
              disabled={processing}
              className={errors.cvc ? 'error' : ''}
              required
            />
            {errors.cvc && <span className="error-message">{errors.cvc}</span>}
          </div>
        </div>

        <div className="payment-summary">
          <div className="summary-row">
            <span>Zu zahlender Betrag:</span>
            <span className="amount">€{(amount / 100).toFixed(2)}</span>
          </div>
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            onClick={onCancel}
            className="cancel-button"
            disabled={processing}
          >
            Abbrechen
          </button>
          <button 
            type="submit" 
            className="submit-button"
            disabled={processing}
          >
            {processing ? (
              <span className="processing">
                <span className="spinner"></span>
                Verarbeitung...
              </span>
            ) : (
              `€${(amount / 100).toFixed(2)} Jetzt zahlen`
            )}
          </button>
        </div>
      </form>

      <div className="payment-footer">
        <p className="security-info">
          🔒 Ihre Zahlungsinformationen werden sicher verschlüsselt übertragen.
        </p>
        <div className="test-cards-hint">
          <details>
            <summary>Test-Karten für Demo</summary>
            <ul>
              <li>✅ Erfolg: 4242 4242 4242 4242</li>
              <li>❌ Fehler: 4000 0000 0000 0002</li>
              <li>🔐 3D Secure: 4000 0027 6000 3184</li>
            </ul>
          </details>
        </div>
      </div>
    </div>
  );
};

export default StripePayment;
